import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Typography,
    Box,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { FunnelSlide, FunnelSlideRegenerateRequest } from '../../types';

interface FunnelSlideRegenerateModalProps {
    open: boolean;
    onClose: () => void;
    slide: FunnelSlide | null;
    onRegenerate: (request: FunnelSlideRegenerateRequest) => void;
    isLoading: boolean;
}

const SLIDE_TYPES = [
    'hero',
    'problem_solution',
    'features',
    'social_proof',
    'cta',
    'character_intro',
    'problem_situation',
    'discovery',
    'transformation',
    'happy_ending',
    'what_is_it',
    'why_you_need',
    'how_it_works',
    'step_by_step',
    'results',
    'get_started',
];

const FunnelSlideRegenerateModal: React.FC<FunnelSlideRegenerateModalProps> = ({
                                                                                   open,
                                                                                   onClose,
                                                                                   slide,
                                                                                   onRegenerate,
                                                                                   isLoading,
                                                                               }) => {
    const [newPrompt, setNewPrompt] = useState<string>('');
    const [slideType, setSlideType] = useState<string>(slide?.slide_type || '');
    const [extraInstructions, setExtraInstructions] = useState<string>('');

    React.useEffect(() => {
        if (slide) {
            setNewPrompt(slide.prompt || '');
            setSlideType(slide.slide_type || '');
            setExtraInstructions('');
        }
    }, [slide]);

    const handleSubmit = () => {
        if (!slide) return;

        const request: FunnelSlideRegenerateRequest = {
            slide_position: slide.position,
            new_prompt: newPrompt || undefined,
            slide_type: slideType || undefined,
            extra_instructions: extraInstructions || undefined,
        };

        onRegenerate(request);
    };

    const handleClose = () => {
        if (!isLoading) {
            onClose();
        }
    };

    if (!slide) {
        return null;
    }

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center' }}>
                <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6">
                        Regenerate Slide {slide.position}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Current type: {slide.slide_type}
                    </Typography>
                </Box>
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    disabled={isLoading}
                    sx={{ color: (theme) => theme.palette.grey[500] }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                <Box sx={{ mb: 3 }}>
                    {slide.image_url && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                                Current Slide:
                            </Typography>
                            <img
                                src={slide.image_url}
                                alt={`Current slide ${slide.position}`}
                                style={{
                                    width: '100%',
                                    maxWidth: 300,
                                    height: 'auto',
                                    borderRadius: 8,
                                    border: '1px solid #ddd',
                                }}
                            />
                        </Box>
                    )}

                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Slide Type</InputLabel>
                        <Select
                            value={slideType}
                            label="Slide Type"
                            onChange={(e) => setSlideType(e.target.value)}
                            disabled={isLoading}
                        >
                            {SLIDE_TYPES.map((type) => (
                                <MenuItem key={type} value={type}>
                                    {type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Custom Prompt (optional)"
                        value={newPrompt}
                        onChange={(e) => setNewPrompt(e.target.value)}
                        disabled={isLoading}
                        sx={{ mb: 2 }}
                        placeholder="Leave empty to use AI-generated prompt for the slide type"
                        helperText="Modify the prompt if you want specific changes to this slide"
                    />

                    <TextField
                        fullWidth
                        multiline
                        rows={2}
                        label="Extra Instructions (optional)"
                        value={extraInstructions}
                        onChange={(e) => setExtraInstructions(e.target.value)}
                        disabled={isLoading}
                        placeholder="e.g., make it more colorful, add text overlay, focus on quality"
                        helperText="Additional instructions for slide generation"
                    />
                </Box>
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose} disabled={isLoading}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={isLoading}
                >
                    {isLoading ? 'Regenerating...' : 'Regenerate Slide'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default FunnelSlideRegenerateModal;
